import { SORT_OPTIONS } from '@/modules/pmt/constants';
import type { IShipment } from '@/modules/pmt/interfaces';
import { isAfterThisWeek, isBeforeToday, isToday, isTomorrow, isWithinThisWeek } from '@/modules/pmt/utils';
import { motion } from 'framer-motion';
import type { JSX, ReactElement } from 'react';
import { ShipmentAds } from '../../ShipmentAds';
import { ShipmentCard } from '../../ShipmentCard';
import { ShipmentSeparator } from '../../ShipmentSeparator';
import { ShipmentWrapper } from '../Shipments.styles';

const itemAnimationsConfigurations = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { type: 'tween', stiffness: 900, damping: 40 },
};

const buildLatestUpdatedTemplate = (acc: Record<string, ReactElement[]>, Component: JSX.Element) => {
  acc.all.push(Component);
  return acc;
};

const buildLatestAddedTemplate = (acc: Record<string, ReactElement[]>, Component: JSX.Element, shipment: IShipment) => {
  switch (true) {
    case isToday(shipment.data?.lastSearched): {
      !acc.recentlyAdded.length &&
        acc.recentlyAdded.push(
          <ShipmentSeparator type='RecentlyAdded' key='recently-added-separator' id='recently-added-separator' />,
        );
      acc.recentlyAdded.push(Component);
      return acc;
    }

    case isBeforeToday(shipment.data?.lastSearched): {
      acc.recentlyAdded.length &&
        !acc.past.length &&
        acc.past.push(<ShipmentSeparator color='grey' type='Past' key='past-separator' id='past-separator' />);
      acc.past.push(Component);
      return acc;
    }

    default:
      return acc;
  }
};

const buildEDDTemplate = (acc: Record<string, ReactElement[]>, Component: JSX.Element, shipment: IShipment) => {
  const edd = shipment.data?.estimatedDeliveryDate;

  switch (true) {
    case !edd: {
      if (!acc.pendingDeliveryDate.length)
        acc.pendingDeliveryDate.push(
          <ShipmentSeparator
            type='PendingDeliveryDate'
            key='pending-delivery-date-separator'
            id='pending-delivery-date-separator'
          />,
        );
      acc.pendingDeliveryDate.push(Component);
      return acc;
    }

    case isToday(edd): {
      if (!acc.today.length)
        acc.today.push(
          <ShipmentSeparator type='ArrivingToday' key='arriving-today-separator' id='arriving-today-separator' />,
        );
      acc.today.push(Component);
      return acc;
    }

    case isTomorrow(edd): {
      if (!acc.tomorrow.length)
        acc.tomorrow.push(
          <ShipmentSeparator
            type='ArrivingTomorrow'
            key='arriving-tomorrow-separator'
            id='arriving-tomorrow-separator'
          />,
        );
      acc.tomorrow.push(Component);
      return acc;
    }

    case isWithinThisWeek(edd) && !isToday(edd) && !isTomorrow(edd): {
      if (!acc.thisWeek.length)
        acc.thisWeek.push(
          <ShipmentSeparator
            type='ArrivingThisWeek'
            key='arriving-this-week-separator'
            id='arriving-this-week-separator'
          />,
        );
      acc.thisWeek.push(Component);
      return acc;
    }

    case isAfterThisWeek(edd): {
      if (!acc.soon.length)
        acc.soon.push(
          <ShipmentSeparator type='ArrivingSoon' key='arriving-soon-separator' id='arriving-soon-separator' />,
        );

      acc.soon.push(Component);
      return acc;
    }

    default:
      return acc;
  }
};

export const buildShipmentsTemplates = (shipments: IShipment[], currentSort: SORT_OPTIONS) => {
  const shipmentsBySeparators = shipments?.reduce(
    (acc: Record<string, JSX.Element[]>, shipment: IShipment, idx) => {
      const Component = (
        <ShipmentWrapper key={shipment.data.firestoreDocId}>
          <motion.div {...itemAnimationsConfigurations} layout>
            <ShipmentCard shipment={shipment} />
          </motion.div>
          <ShipmentAds idx={idx} />
        </ShipmentWrapper>
      );

      if (currentSort === SORT_OPTIONS.LATEST_UPDATED) {
        buildLatestUpdatedTemplate(acc, Component);
      } else if (currentSort === SORT_OPTIONS.LATEST_ADDED) {
        buildLatestAddedTemplate(acc, Component, shipment);
      } else if (currentSort === SORT_OPTIONS.DELIVERY_DATE) {
        buildEDDTemplate(acc, Component, shipment);
      }
      return acc;
    },
    {
      recentlyAdded: [],
      past: [],
      today: [],
      tomorrow: [],
      thisWeek: [],
      soon: [],
      pendingDeliveryDate: [],
      all: [],
    },
  );

  return shipmentsBySeparators;
};
